import { useId, useState } from "react";
import type { KeyboardEvent, ReactElement } from "react";

import { useTranslation } from "../../i18n";
import styles from "./Combobox.module.css";

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
  /** Secondary text on the right (the mockup picker's context size or price). */
  readonly description?: string | undefined;
}

export interface ComboboxProps {
  readonly id: string;
  /** The accessible name of the field and of its listbox (from `t()`). */
  readonly label: string;
  readonly options: readonly ComboboxOption[];
  /** The chosen option, or `null` for none. */
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * The options are suggestions, not a closed list (UX-11, the model id): the field keeps whatever
   * is typed, `onChange` reports every keystroke and `value` is the text itself.
   */
  readonly freeText?: boolean | undefined;
  /** The field is refused (`aria-invalid`), e.g. by a form's validation. */
  readonly invalid?: boolean | undefined;
  /** The text cannot change for now (a save in flight); the suggestions stay closed. */
  readonly readOnly?: boolean | undefined;
}

/** Lower-case and strip the accents, so "cafe" finds "Café" (Spanish text, neutral search). */
export function normaliseSearch(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLocaleLowerCase("es");
}

/**
 * A searchable list (the mockup's model picker `.pk-*`): the WAI-ARIA 1.2 combobox with a listbox
 * popup and list autocomplete.
 *
 * The focus never leaves the field: `aria-activedescendant` points at the active option, which carries
 * `aria-selected`. Typing filters (case- and accent-insensitive) and opens; ArrowDown/ArrowUp open on
 * the first/last option and move with wrap; Enter or a press chooses; Escape and leaving the field
 * collapse without choosing and put the chosen label back. A short fixed list is a `Select` instead.
 * With `freeText` the options only suggest: the typed text is the value and stays when the list
 * collapses, and choosing a suggestion replaces it.
 */
export function Combobox({
  id,
  label,
  options,
  value,
  onChange,
  describedBy,
  disabled = false,
  freeText = false,
  invalid = false,
  readOnly = false,
}: ComboboxProps): ReactElement {
  const { t } = useTranslation();
  const listboxId = useId();
  const chosenLabel = freeText ? (value ?? "") : (options.find((option) => option.value === value)?.label ?? "");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);

  const visible =
    query === null ? options : options.filter((option) => normaliseSearch(option.label).includes(normaliseSearch(query)));
  const activeOption = open ? visible[active] : undefined;
  const optionId = (index: number): string => `${listboxId}-option-${String(index)}`;

  const collapse = (): void => {
    setOpen(false);
    setQuery(null);
    setActive(-1);
  };

  const choose = (option: ComboboxOption): void => {
    collapse();
    onChange(option.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (readOnly) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const count = visible.length;
      if (count === 0) {
        setOpen(true);
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      const start = event.key === "ArrowDown" ? 0 : count - 1;
      setActive(!open || active < 0 ? start : (active + step + count) % count);
      setOpen(true);
    } else if (event.key === "Enter") {
      if (!open) return;
      if (freeText && activeOption === undefined) {
        // The typed text is the answer: collapse and let the form have its Enter.
        collapse();
        return;
      }
      event.preventDefault();
      if (activeOption !== undefined) choose(activeOption);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      collapse();
    }
  };

  return (
    <div className={styles.combobox}>
      <input
        id={id}
        className={styles.input}
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open && visible.length > 0 ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={activeOption === undefined ? undefined : optionId(active)}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        autoComplete="off"
        disabled={disabled}
        readOnly={readOnly}
        value={freeText ? chosenLabel : (query ?? chosenLabel)}
        onChange={(event) => {
          setQuery(event.target.value);
          // A suggestion is never pre-chosen over free text: Enter must keep what was typed.
          setActive(freeText ? -1 : 0);
          setOpen(true);
          if (freeText) onChange(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={collapse}
      />
      {/* Always mounted, so the empty result is announced when it appears (verifier m5). */}
      <div role="status" className={styles.status}>
        {open && visible.length === 0 ? t("common.state.noResults") : ""}
      </div>
      {open && visible.length > 0 ? (
        <div className={styles.popup}>
          <ul id={listboxId} role="listbox" aria-label={label} className={styles.listbox}>
            {visible.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                className={styles.option}
                // Chosen on press: `preventDefault` keeps the focus in the field, whose blur would
                // otherwise collapse the list first. The keyboard path is the field's own handler.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
              >
                <span className={styles.optionLabel}>{option.label}</span>
                {option.description === undefined ? null : (
                  <span className={styles.description}>{option.description}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
